import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  BUILD_MANAGEMENT_ENTRY_LABEL,
  BUILD_MANAGEMENT_SECTIONS,
  type BuildManagementSectionId,
  isBuildManagementSectionId,
} from '../../types/buildManagement';
import Modal from '../ui/Modal';
import Button from '../ui/Button';

export type ManageBuildActionId =
  | 'add-part'
  | 'import'
  | 'enhance'
  | 'link-media'
  | 'add-photos'
  | 'share-card'
  | 'qr-sticker'
  | 'passport-page';

export interface ManageBuildShellProps {
  isOpen: boolean;
  onClose: () => void;
  initialSection?: BuildManagementSectionId | string | null;
  vehicleTitle: string;
  completionPercent?: number;
  missingLabels?: string[];
  onTrack?: (event: string, metadata?: Record<string, unknown>) => void;
  /** Called when the sticky nav or overview jumps to a section (for URL sync). */
  onSectionChange?: (section: BuildManagementSectionId) => void;
  /** Inline editors rendered under each section heading. */
  sectionPanels: Partial<Record<BuildManagementSectionId, React.ReactNode>>;
}

function sectionDomId(id: BuildManagementSectionId): string {
  return `manage-section-${id}`;
}

const ManageBuildShell: React.FC<ManageBuildShellProps> = ({
  isOpen,
  onClose,
  initialSection = 'overview',
  vehicleTitle,
  completionPercent,
  missingLabels = [],
  onTrack,
  onSectionChange,
  sectionPanels,
}) => {
  const titleId = useId();
  const scrollRef = useRef<HTMLDivElement>(null);
  const suppressSpyRef = useRef(false);
  const resolvedInitial = useMemo(() => {
    if (initialSection && isBuildManagementSectionId(initialSection)) return initialSection;
    return 'overview';
  }, [initialSection]);
  const [activeSection, setActiveSection] = useState<BuildManagementSectionId>(resolvedInitial);
  const [visitedSections, setVisitedSections] = useState<Set<BuildManagementSectionId>>(
    () => new Set<BuildManagementSectionId>(['overview', resolvedInitial])
  );
  const wasOpenRef = useRef(false);
  const onTrackRef = useRef(onTrack);
  onTrackRef.current = onTrack;
  const onSectionChangeRef = useRef(onSectionChange);
  onSectionChangeRef.current = onSectionChange;

  const markVisited = (id: BuildManagementSectionId) => {
    setVisitedSections((prev) => {
      if (prev.has(id)) return prev;
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  };

  const scrollToSection = (id: BuildManagementSectionId, behavior: ScrollBehavior = 'smooth') => {
    markVisited(id);
    setActiveSection(id);
    onSectionChangeRef.current?.(id);
    suppressSpyRef.current = true;
    // Wait a frame so lazy-mounted section panels can layout before scrolling.
    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        const root = scrollRef.current;
        const target = root?.querySelector<HTMLElement>(`#${sectionDomId(id)}`);
        if (!root || !target) {
          suppressSpyRef.current = false;
          return;
        }
        const top = Math.max(0, target.offsetTop - 8);
        try {
          if (typeof root.scrollTo === 'function') {
            root.scrollTo({ top, behavior });
          } else {
            root.scrollTop = top;
          }
        } catch {
          root.scrollTop = top;
        }
        window.setTimeout(() => {
          suppressSpyRef.current = false;
        }, behavior === 'smooth' ? 450 : 50);
      }, 40);
    });
  };

  // Seed section only when the shell opens (false → true).
  useEffect(() => {
    if (!isOpen) {
      wasOpenRef.current = false;
      return;
    }
    if (wasOpenRef.current) return;
    wasOpenRef.current = true;
    setActiveSection(resolvedInitial);
    setVisitedSections(new Set<BuildManagementSectionId>(['overview', resolvedInitial]));
    onTrackRef.current?.('manage_build_opened', { section: resolvedInitial });
    // Scroll after layout so section anchors exist.
    const timer = window.setTimeout(() => {
      scrollToSection(resolvedInitial, 'auto');
    }, 30);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally seed once per open
  }, [isOpen, resolvedInitial]);

  useEffect(() => {
    if (!isOpen || !wasOpenRef.current) return;
    onTrackRef.current?.('manage_build_section_opened', { section: activeSection });
  }, [isOpen, activeSection]);

  // Scroll-spy: highlight the section nearest the top of the scrollport.
  useEffect(() => {
    if (!isOpen) return undefined;
    const root = scrollRef.current;
    if (!root) return undefined;

    const nodes = BUILD_MANAGEMENT_SECTIONS.map((section) =>
      root.querySelector<HTMLElement>(`#${sectionDomId(section.id)}`)
    ).filter((node): node is HTMLElement => Boolean(node));

    if (nodes.length === 0) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        if (suppressSpyRef.current) return;
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const top = visible[0];
        if (!top?.target?.id) return;
        const id = top.target.id.replace(/^manage-section-/, '');
        if (!isBuildManagementSectionId(id)) return;
        setActiveSection((prev) => {
          if (prev === id) return prev;
          onSectionChangeRef.current?.(id);
          return id;
        });
        markVisited(id);
      },
      {
        root,
        rootMargin: '-8% 0px -70% 0px',
        threshold: [0, 0.1, 0.25],
      }
    );

    nodes.forEach((node) => observer.observe(node));
    return () => observer.disconnect();
  }, [isOpen, sectionPanels]);

  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="xl"
      title={BUILD_MANAGEMENT_ENTRY_LABEL}
      description={`Edit ${vehicleTitle} without digging through menus.`}
      showClose
      closeOnOverlayClick
      className="!max-w-5xl w-full !max-h-[92vh] !overflow-hidden flex flex-col p-4 sm:p-6"
    >
      <div className="grid grid-cols-1 md:grid-cols-[200px_minmax(0,1fr)] gap-4 md:gap-6 min-h-0 flex-1">
        <nav
          aria-label="Build management sections"
          className="md:sticky md:top-0 md:self-start md:max-h-[min(70vh,calc(92vh-8rem))] md:overflow-y-auto md:border-r md:border-white/[0.06] md:pr-3"
        >
          <ul className="flex md:flex-col gap-1 overflow-x-auto pb-1 md:pb-0 -mx-1 px-1">
            {BUILD_MANAGEMENT_SECTIONS.map((item) => {
              const selected = item.id === activeSection;
              return (
                <li key={item.id} className="shrink-0 md:shrink md:w-full">
                  <button
                    type="button"
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full text-left min-h-[44px] px-3 py-2 rounded-lg text-sm transition-colors ${
                      selected
                        ? 'bg-white/[0.08] text-white'
                        : 'text-gray-400 hover:text-white hover:bg-white/[0.04]'
                    } ${item.danger ? 'md:mt-2' : ''}`}
                    aria-current={selected ? 'true' : undefined}
                  >
                    {item.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </nav>

        <div
          ref={scrollRef}
          className="min-w-0 min-h-[50vh] max-h-[min(70vh,calc(92vh-8rem))] overflow-y-auto overscroll-contain pr-1"
          tabIndex={0}
          role="region"
          aria-labelledby={titleId}
        >
          <h3 id={titleId} className="sr-only">
            Manage build sections
          </h3>

          {BUILD_MANAGEMENT_SECTIONS.map((item) => (
            <section
              key={item.id}
              id={sectionDomId(item.id)}
              aria-labelledby={`${sectionDomId(item.id)}-heading`}
              className="scroll-mt-2 pb-10 mb-8 border-b border-white/[0.06] last:border-b-0 last:mb-0 last:pb-4"
            >
              <header className="mb-4">
                <h4
                  id={`${sectionDomId(item.id)}-heading`}
                  className={`text-lg font-semibold ${item.danger ? 'text-red-300' : 'text-white'}`}
                >
                  {item.label}
                </h4>
                <p className="text-sm text-gray-400 mt-1">{item.description}</p>
              </header>

              {item.id === 'overview' ? (
                <div className="space-y-4">
                  {typeof completionPercent === 'number' ? (
                    <div className="rounded-xl border border-white/[0.06] bg-[#0c0c0c]/60 p-4">
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <p className="text-sm text-gray-200">Documentation progress</p>
                        <span className="font-mono text-xs text-gray-300 tabular-nums">
                          {completionPercent}%
                        </span>
                      </div>
                      <div className="h-1.5 w-full rounded-full bg-white/[0.06] overflow-hidden">
                        <div
                          className="h-full rounded-full bg-orange-500/80"
                          style={{ width: `${Math.max(0, Math.min(100, completionPercent))}%` }}
                        />
                      </div>
                      {missingLabels.length > 0 ? (
                        <p className="text-xs text-gray-400 mt-2">
                          Next: add {missingLabels.slice(0, 3).join(', ')}
                          {missingLabels.length > 3 ? '…' : ''}.
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400 mt-2">Core identity fields look complete.</p>
                      )}
                    </div>
                  ) : null}

                  <div className="flex flex-wrap gap-2">
                    {BUILD_MANAGEMENT_SECTIONS.filter((section) => section.id !== 'overview').map(
                      (section) => (
                        <Button
                          key={section.id}
                          type="button"
                          variant="secondary"
                          className="min-h-[44px]"
                          onClick={() => scrollToSection(section.id)}
                        >
                          {section.label}
                        </Button>
                      )
                    )}
                  </div>
                  {sectionPanels.overview}
                </div>
              ) : visitedSections.has(item.id) ? (
                sectionPanels[item.id] ?? (
                  <p className="text-sm text-gray-500">Nothing to edit in this section yet.</p>
                )
              ) : (
                <p className="text-sm text-gray-500">
                  Select this section in the sidebar to load its editors.
                </p>
              )}
            </section>
          ))}

          <div className="pt-2 flex justify-end">
            <Button type="button" variant="ghost" onClick={onClose} className="min-h-[44px]">
              Done
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ManageBuildShell;
